// ============================================================================
// Group API
// ============================================================================

import { supabase } from 'app/auth/auth.js';

// Group Operations
// ============================================================================
const GROUP_SELECT = 'id, group_name, portfolio_manager, profiles(full_name, short_name)';

/**
 * The ONE deep link into the client directory filtered to a group. The
 * directory seeds its grid filter from `?group=` before the first load, and
 * `groups.group_name` is unique, so the name is a safe identity for it.
 * @param {string} groupName
 * @returns {string} hash href
 */
export function groupDirectoryLink(groupName)
{
    return `#/clients?group=${encodeURIComponent(groupName || '')}`;
}

/**
 * Search groups with optional filter
 * @param {string} query - Search query
 * @param {object} [options]
 * @param {boolean} [options.withMemberCount=false] - Also return `member_count`
 *   per group (one PostgREST count embed). Opt-in because the dropdown store
 *   calls this at boot and on every realtime groups change, and only the
 *   groups page shows the count.
 * @returns {Promise<{data: array|null, error: Error|null}>}
 */
export async function searchGroups(query = '', { withMemberCount = false } = {})
{
    try
    {
        const { data, error } = await supabase
            .from('groups')
            .select(withMemberCount ? `${GROUP_SELECT}, clients(count)` : GROUP_SELECT)
            .order('group_name');
        if (error) throw error;
        const list = (data || []).map(g =>
        {
            if (!withMemberCount) return g;
            const { clients, ...group } = g;
            return { ...group, member_count: clients?.[0]?.count ?? 0 };
        });
        if (!query) return { data: list, error: null };
        const q = query.toLowerCase();
        const filtered = list.filter(g =>
        {
            const parts = [
                g.group_name || '',
                g.profiles?.full_name || '',
                g.profiles?.short_name || ''
            ].join(' ').toLowerCase();
            return parts.includes(q);
        });
        return { data: filtered, error: null };
    } catch (error)
    {
        console.error(`[clientApi] Failed to search groups:`, error);
        return { data: null, error };
    }
}
/**
 * Create a new group
 * @param {object} groupData - Group data object
 * @returns {Promise<{data: object|null, error: Error|null}>}
 */
export async function createGroup(groupData)
{
    try
    {
        const { data, error } = await supabase
            .from('groups')
            .insert(groupData)
            .select(GROUP_SELECT)
            .single();
        if (error) throw error;
        return { data, error: null };
    } catch (error)
    {
        console.error(`[clientApi] Failed to create group:`, error);
        return { data: null, error };
    }
}
/**
 * Update a group
 * @param {string} groupId - The group ID
 * @param {object} updates - Object with fields to update
 * @returns {Promise<{data: object|null, error: Error|null}>}
 */
export async function updateGroup(groupId, updates)
{
    try
    {
        const { data, error } = await supabase
            .from('groups')
            .update(updates)
            .eq('id', groupId)
            .select(GROUP_SELECT)
            .single();
        if (error) throw error;
        return { data, error: null };
    } catch (error)
    {
        console.error(`[clientApi] Failed to update group:`, error);
        return { data: null, error };
    }
}
/**
 * Delete a group
 * @param {string} groupId - The group ID
 * @returns {Promise<{data: object|null, error: Error|null}>}
 */
export async function deleteGroup(groupId)
{
    try
    {
        const { data, error } = await supabase
            .from('groups')
            .delete()
            .eq('id', groupId);
        if (error) throw error;
        return { data, error: null };
    } catch (error)
    {
        console.error(`[clientApi] Failed to delete group:`, error);
        return { data: null, error };
    }
}
